<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
      <!-- Logo -->
      <div class="text-center mb-8">
        <div class="w-14 h-14 bg-[#8C0004] rounded-xl flex items-center justify-center mx-auto mb-4">
          <Icon icon="solar:code-square-bold" class="w-8 h-8 text-white" />
        </div>
        <h1 class="text-2xl font-bold text-gray-900">Portail Partenaire</h1>
        <p class="text-sm text-gray-500 mt-1">API de livraison Youdégo</p>
      </div>

      <!-- Étape 1 : téléphone -->
      <form v-if="step === 'phone'" @submit.prevent="requestOtp" class="space-y-5">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Numéro WhatsApp
          </label>
          <input
            v-model="phone"
            type="tel"
            maxlength="10"
            placeholder="0XXXXXXXXX"
            required
            class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
          />
          <p class="text-xs text-gray-500 mt-2">
            Le numéro associé à votre compte partenaire. Vous recevrez un code de connexion sur WhatsApp.
          </p>
        </div>

        <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="text-sm text-red-600">{{ error }}</p>
        </div>

        <button
          type="submit"
          :disabled="!validPhone || loading"
          class="w-full px-6 py-3 rounded-lg bg-[#8C0004] text-white font-medium hover:bg-red-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          <Icon v-if="loading" icon="svg-spinners:ring-resize" class="w-4 h-4" />
          {{ loading ? 'Envoi...' : 'Recevoir le code' }}
        </button>
      </form>

      <!-- Étape 2 : OTP -->
      <form v-else @submit.prevent="verifyOtp" class="space-y-5">
        <div>
          <p class="text-sm text-gray-600 mb-4 text-center">
            Entrez le code à 6 chiffres envoyé au <strong>{{ phone }}</strong>
          </p>
          <div class="flex justify-center gap-2">
            <input
              v-for="(_, i) in 6"
              :key="i"
              :ref="(el) => setOtpRef(el, i)"
              v-model="otp[i]"
              @input="handleOtpInput($event, i)"
              @keydown="handleKeydown($event, i)"
              @paste="handlePaste"
              type="text"
              inputmode="numeric"
              maxlength="1"
              class="w-11 h-13 py-3 text-center text-xl font-bold rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#8C0004] focus:border-transparent"
            />
          </div>
        </div>

        <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="text-sm text-red-600">{{ error }}</p>
        </div>

        <button
          type="submit"
          :disabled="!otpComplete || loading"
          class="w-full px-6 py-3 rounded-lg bg-[#8C0004] text-white font-medium hover:bg-red-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          <Icon v-if="loading" icon="svg-spinners:ring-resize" class="w-4 h-4" />
          {{ loading ? 'Vérification...' : 'Se connecter' }}
        </button>

        <button
          type="button"
          @click="backToPhone"
          class="w-full px-6 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Changer de numéro
        </button>
      </form>

      <p class="text-center text-xs text-gray-400 mt-8">
        Pas encore partenaire ?
        <a href="https://youdego.app/partenaire" class="text-[#8C0004] hover:underline">
          Déposer une candidature
        </a>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Icon } from '@iconify/vue'
import { authClient, apiFor } from '../services/api'
import { useAuthStore } from '../store/auth'

const router = useRouter()
const authStore = useAuthStore()

const step = ref<'phone' | 'otp'>('phone')
const phone = ref('')
const otp = ref(['', '', '', '', '', ''])
const userId = ref('')
const error = ref('')
const loading = ref(false)
let otpRefs: any[] = []

const validPhone = computed(() => /^0[0-9]{9}$/.test(phone.value))
const otpComplete = computed(() => otp.value.every((v) => v))

onMounted(async () => {
  // Session déjà ouverte : aller directement au tableau de bord
  if (await authStore.checkAuth()) {
    router.push('/dashboard')
  }
})

function setOtpRef(el: any, index: number) {
  if (el) otpRefs[index] = el
}

function handleOtpInput(event: any, index: number) {
  const value = event.target.value
  if (value && !/^\d$/.test(value)) {
    otp.value[index] = ''
    return
  }
  if (value && index < 5) {
    otpRefs[index + 1]?.focus()
  }
}

function handleKeydown(event: any, index: number) {
  if (event.key === 'Backspace' && !otp.value[index] && index > 0) {
    otpRefs[index - 1]?.focus()
  }
}

function handlePaste(event: any) {
  const paste = event.clipboardData.getData('text')
  if (/^\d{6}$/.test(paste)) {
    otp.value = paste.split('')
  }
}

async function requestOtp() {
  if (!validPhone.value) return
  loading.value = true
  error.value = ''

  try {
    const { data } = await authClient.post('/partner-portal/auth/login', {
      phone: phone.value,
    })

    if (!data.userId) {
      // Réponse générique : le numéro ne correspond à aucun compte partenaire.
      // On l'annonce clairement plutôt que de bloquer l'utilisateur sur un
      // écran OTP qui n'aboutira jamais.
      error.value =
        "Aucun compte partenaire n'est associé à ce numéro. Vérifiez le numéro ou déposez une candidature."
      return
    }

    userId.value = data.userId
    step.value = 'otp'
  } catch (err: any) {
    error.value =
      err.response?.data?.message || 'Erreur lors de l\'envoi du code. Réessayez.'
  } finally {
    loading.value = false
  }
}

async function verifyOtp() {
  if (!otpComplete.value) return
  loading.value = true
  error.value = ''

  const code = otp.value.join('')

  try {
    // Ouvrir la session sur la PROD (source de vérité des comptes)…
    const { data } = await authClient.post('/partner-portal/auth/verify-otp', {
      userId: userId.value,
      otp: code,
    })

    authStore.setSession(data.user, data.partner)
    router.push('/dashboard')
  } catch (err: any) {
    error.value = err.response?.data?.message || 'Code invalide ou expiré'
    otp.value = ['', '', '', '', '', '']
    otpRefs[0]?.focus()
  } finally {
    loading.value = false
  }
}

function backToPhone() {
  step.value = 'phone'
  otp.value = ['', '', '', '', '', '']
  error.value = ''
}
</script>
